import React, { forwardRef } from "react";
import "./MelaChip.css";
import { MelaTooltip } from "../MelaTooltip";
import type { MelaTooltipProps } from "../MelaTooltip";

export type MelaChipTone =
  | "neutral"
  | "primary"
  | "success"
  | "warning"
  | "danger"
  | "ai";

export type MelaChipVariant = "soft" | "outline" | "solid";
export type MelaChipSize = "sm" | "md";

export type MelaChipPalette = Partial<{
  backgroundColor: string;
  borderColor: string;
  textColor: string;
  dotColor: string;
}>;

export interface MelaChipProps
  extends Omit<React.HTMLAttributes<HTMLSpanElement>, "children"> {
  children?: React.ReactNode;
  derivePaletteFromDotColor?: boolean;
  dotColor?: string;
  endIcon?: React.ReactNode;
  palette?: MelaChipPalette;
  pulse?: boolean;
  size?: MelaChipSize;
  startIcon?: React.ReactNode;
  tone?: MelaChipTone;
  truncate?: boolean;
  truncateAt?: number;
  variant?: MelaChipVariant;
  dataTestId?: string;
  tooltip?: React.ReactNode;
  tooltipOnTruncate?: boolean;
  tooltipProps?: Omit<MelaTooltipProps, "children" | "content">;
}

type ChipPalette = {
  backgroundColor: string;
  borderColor: string;
  textColor: string;
  dotColor: string;
};

type ChipSizeConfig = {
  dotSize: number;
  fontSize: string;
  fontWeight: number;
  gap: number;
  minHeight: number;
  paddingX: number;
};

const DEFAULT_TONE: MelaChipTone = "neutral";
const DEFAULT_VARIANT: MelaChipVariant = "soft";
const DEFAULT_SIZE: MelaChipSize = "sm";
const HEX_COLOR_PATTERN = /^#[0-9a-f]{6}$/i;

const getSoftColorFromDotColor = (color: string) =>
  HEX_COLOR_PATTERN.test(color)
    ? `${color}18`
    : `color-mix(in srgb, ${color} 12%, transparent)`;

const getPaletteFromDotColor = (color: string): ChipPalette => ({
  backgroundColor: getSoftColorFromDotColor(color),
  borderColor: color,
  textColor: color,
  dotColor: color,
});

const getSemanticPalette = (
  tone: MelaChipTone,
  variant: MelaChipVariant,
): ChipPalette => {
  const softPalettes: Record<MelaChipTone, ChipPalette> = {
    neutral: {
      backgroundColor: "var(--mela-color-neutral-soft-bg)",
      borderColor: "var(--mela-color-neutral-soft-border)",
      textColor: "var(--mela-color-neutral-soft-text)",
      dotColor: "var(--mela-color-neutral-soft-text)",
    },
    primary: {
      backgroundColor: "var(--mela-color-primary-soft-bg)",
      borderColor: "var(--mela-color-primary-soft-border)",
      textColor: "var(--mela-color-primary-soft-text)",
      dotColor: "var(--mela-color-primary-soft-text)",
    },
    success: {
      backgroundColor: "var(--mela-color-success-soft-bg)",
      borderColor: "var(--mela-color-success-soft-border)",
      textColor: "var(--mela-color-success-soft-text)",
      dotColor: "var(--mela-color-success-soft-text)",
    },
    warning: {
      backgroundColor: "var(--mela-color-warning-soft-bg)",
      borderColor: "var(--mela-color-warning-soft-border)",
      textColor: "var(--mela-color-warning-soft-text)",
      dotColor: "var(--mela-color-warning-soft-text)",
    },
    danger: {
      backgroundColor: "var(--mela-color-danger-soft-bg)",
      borderColor: "var(--mela-color-danger-soft-border)",
      textColor: "var(--mela-color-danger-soft-text)",
      dotColor: "var(--mela-color-danger-soft-text)",
    },
    ai: {
      backgroundColor: "var(--mela-color-ai-soft-bg)",
      borderColor: "var(--mela-color-ai-soft-border)",
      textColor: "var(--mela-color-ai-soft-text)",
      dotColor: "var(--mela-color-ai-soft-text)",
    },
  };

  if (variant === "soft") {
    return softPalettes[tone];
  }

  if (variant === "outline") {
    const softPalette = softPalettes[tone];

    return {
      backgroundColor: "transparent",
      borderColor: softPalette.borderColor,
      textColor: softPalette.textColor,
      dotColor: softPalette.dotColor,
    };
  }

  if (tone === "primary") {
    return {
      backgroundColor: "var(--mela-color-primary-solid-bg)",
      borderColor: "var(--mela-color-primary-solid-border)",
      textColor: "var(--mela-color-primary-solid-text)",
      dotColor: "var(--mela-color-primary-solid-text)",
    };
  }

  if (tone === "success") {
    return {
      backgroundColor: "var(--mela-color-success-solid-bg)",
      borderColor: "var(--mela-color-success-solid-border)",
      textColor: "var(--mela-color-success-solid-text)",
      dotColor: "var(--mela-color-success-solid-text)",
    };
  }

  if (tone === "warning") {
    return {
      backgroundColor: "var(--mela-color-warning-solid-bg)",
      borderColor: "var(--mela-color-warning-solid-border)",
      textColor: "var(--mela-color-warning-solid-text)",
      dotColor: "var(--mela-color-warning-solid-text)",
    };
  }

  if (tone === "danger") {
    return {
      backgroundColor: "var(--mela-color-danger-solid-bg)",
      borderColor: "var(--mela-color-danger-solid-border)",
      textColor: "var(--mela-color-danger-solid-text)",
      dotColor: "var(--mela-color-danger-solid-text)",
    };
  }

  if (tone === "ai") {
    return {
      backgroundColor: "var(--mela-color-ai-solid-bg)",
      borderColor: "var(--mela-color-ai-solid-border)",
      textColor: "var(--mela-color-ai-solid-text)",
      dotColor: "var(--mela-color-ai-solid-text)",
    };
  }

  return {
    backgroundColor: "var(--mela-color-neutral-solid-bg)",
    borderColor: "var(--mela-color-neutral-solid-border)",
    textColor: "var(--mela-color-neutral-solid-text)",
    dotColor: "var(--mela-color-neutral-solid-text)",
  };
};

const getSizeConfig = (size: MelaChipSize): ChipSizeConfig => {
  switch (size) {
    case "md":
      return {
        dotSize: 8,
        fontSize: "0.76rem",
        fontWeight: 700,
        gap: 6,
        minHeight: 28,
        paddingX: 10,
      };
    case "sm":
    default:
      return {
        dotSize: 7,
        fontSize: "0.7rem",
        fontWeight: 700,
        gap: 6,
        minHeight: 24,
        paddingX: 9,
      };
  }
};

type MelaChipCssVariables = React.CSSProperties & {
  ["--mela-chip-background-color"]?: string;
  ["--mela-chip-border-color"]?: string;
  ["--mela-chip-dot-color"]?: string;
  ["--mela-chip-dot-size"]?: string;
  ["--mela-chip-font-size"]?: string;
  ["--mela-chip-font-weight"]?: string;
  ["--mela-chip-gap"]?: string;
  ["--mela-chip-min-height"]?: string;
  ["--mela-chip-padding-x"]?: string;
  ["--mela-chip-text-color"]?: string;
};

export const MelaChip = forwardRef<HTMLSpanElement, MelaChipProps>(
  (
    {
      children,
      className = "",
      derivePaletteFromDotColor = false,
      dotColor,
      endIcon,
      palette,
      pulse = false,
      size = DEFAULT_SIZE,
      startIcon,
      style,
      tone = DEFAULT_TONE,
      truncate = false,
      truncateAt,
      variant = DEFAULT_VARIANT,
      dataTestId,
      tooltip,
      tooltipOnTruncate = false,
      tooltipProps,
      ...props
    },
    ref,
  ) => {
    const semanticPalette = getSemanticPalette(tone, variant);
    const sizeConfig = getSizeConfig(size);
    const resolvedDotColor = dotColor || palette?.dotColor;
    const textChildren =
      typeof children === "string" || typeof children === "number"
        ? String(children)
        : null;
    const isTextTruncated = Boolean(
      textChildren && truncateAt && textChildren.length > truncateAt,
    );
    const displayedChildren =
      textChildren && isTextTruncated
        ? `${textChildren.slice(0, truncateAt).trimEnd()}...`
        : children;
    const basePalette =
      derivePaletteFromDotColor && resolvedDotColor
        ? getPaletteFromDotColor(resolvedDotColor)
        : semanticPalette;

    const chipStyle: MelaChipCssVariables = {
      "--mela-chip-background-color":
        palette?.backgroundColor || basePalette.backgroundColor,
      "--mela-chip-border-color":
        palette?.borderColor || basePalette.borderColor,
      "--mela-chip-text-color": palette?.textColor || basePalette.textColor,
      "--mela-chip-dot-color": resolvedDotColor || basePalette.dotColor,
      "--mela-chip-dot-size": `${sizeConfig.dotSize}px`,
      "--mela-chip-font-size": sizeConfig.fontSize,
      "--mela-chip-font-weight": `${sizeConfig.fontWeight}`,
      "--mela-chip-gap": `${sizeConfig.gap}px`,
      "--mela-chip-min-height": `${sizeConfig.minHeight}px`,
      "--mela-chip-padding-x": `${sizeConfig.paddingX}px`,
      ...style,
    };

    const resolvedTooltip = tooltipOnTruncate
      ? tooltip || textChildren
      : tooltip;
    const shouldShowTooltip =
      Boolean(resolvedTooltip) &&
      !tooltipProps?.disabled &&
      (!tooltipOnTruncate || isTextTruncated);

    const chipNode = (
      <span
        {...props}
        ref={ref}
        className={`mela-chip${pulse ? " mela-chip--pulse" : ""}${truncate ? " mela-chip--truncate" : ""}${className ? ` ${className}` : ""}`}
        data-mela-chip="true"
        data-testid={dataTestId}
        style={chipStyle}
      >
        {resolvedDotColor ? <span className="mela-chip__dot" /> : null}
        {startIcon ? (
          <span className="mela-chip__icon">{startIcon}</span>
        ) : null}
        {displayedChildren ? (
          <span className="mela-chip__label">{displayedChildren}</span>
        ) : null}
        {endIcon ? <span className="mela-chip__icon">{endIcon}</span> : null}
      </span>
    );

    if (!shouldShowTooltip) {
      return chipNode;
    }

    return (
      <MelaTooltip content={resolvedTooltip} triggerAs="span" {...tooltipProps}>
        {chipNode}
      </MelaTooltip>
    );
  },
);

MelaChip.displayName = "MelaChip";
